import { TemplateResult, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import encapsulatedStyles from './styles/outline-ckeditor.inner.pcss?inline';
import globalStyles from './styles/outline-ckeditor.global.pcss?inline';

const componentName = 'outline-ckeditor';
let tableInstructionId = 0;

/**
 * The Outline Ckeditor component
 * @element outline-ckeditor
 * @slot - default slot
 *
 * @notes
 * - This component has two major coalescing CSS files. One is referenced
 * by `../../styles/wysiwyg.pcss` and controls the admin experience.
 * The other is used by this component for rendered content.
 * Additional documentation can be found in `./docs`.
 *
 * - Individual elements have their own files within ./element-specific. In
 *   some cases these are adjustments to other element styles, in other cases it
 *   represents the corpus of styles for that element.
 * - handleFileLinks() appends classes and works with
 *   ./element-specific/link.pcss to append specific icons for linked files.
 * - handleExternalLinks() enhances the accessibility of external links.
 * - Heading styles are tag-agnostic, and imported from <outline-heading>.
 *   Configure ckeditor to apply classes to the selected text so editors may use
 *   heading tags semantically.
 * - Links/buttons are derived from <outline-cta> @todo expand on this.
 * Overrides for icons belong to this component however.
 *
 *
 * @cssproperty
 * --outline-ckeditor--heading-color
 * --outline-ckeditor--text-color
 * --outline-ckeditor--body-text--font-size
 * --outline-ckeditor--accent-color
 * --outline-ckeditor--body-text--margin-bottom
 * --outline-ckeditor--body-text--font-size
 * --outline-ckeditor--body-text--line-height
 * --outline-ckeditor--table--color
 * --outline-ckeditor--table--outline
 * --outline-ckeditor--table--border-radius
 * --outline-ckeditor--table--head-font-size
 * --outline-ckeditor--table--head-font-weight
 * --outline-ckeditor--table--body-font-size
 * --outline-ckeditor--table--body-font-weight
 * --outline-ckeditor--table--background-color-main
 * --outline-ckeditor--table--background-color-offset
 * --outline-ckeditor--table--cell-border
 * --outline-ckeditor--link-icon--spacing
 * --outline-ckeditor--link-icon--size
 * --outline-ckeditor--link-icon--color
 * --outline-ckeditor--link-icon--icon
 * --outline-ckeditor--link-icon--icon-external
 * --outline-ckeditor--link-icon--icon-pdf
 * --outline-ckeditor--link-icon--icon-ppt
 * --outline-ckeditor--link-icon--icon-file
 *
 */
@customElement('outline-ckeditor')
export class OutlineCkeditor extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  @property({ type: Boolean, attribute: 'has-inline-styles', reflect: true })
  hasInlineStyles: boolean;

  connectedCallback(): void {
    super.connectedCallback();
    this.enhanceLightDomContent();
  }

  private handleSlotChange = () => {
    this.enhanceLightDomContent();
  };

  /**
   * Checks the file type of a given hyperlink element and applies appropriate class.
   *
   * This function inspects the "href" and "download" attributes of the provided hyperlink element
   * to determine the file type and adds a class for known types. The classes added are:
   *   - pdf-link: .pdf
   *   - ppt-link: .ppsx, .pptx, .pptm, .potx
   *   - file-link: .eml, .docx, .msg
   *
   * @param {HTMLElement} el - The hyperlink element to check for the file type.
   *
   */
  private handleFileLinks(el: HTMLElement) {
    if (el.querySelector('img')) {
      return;
    }

    const href = el.getAttribute('href');

    if (!href) {
      return;
    }

    const download = el.getAttribute('download');

    if (href.includes('.pdf') || download?.includes('.pdf')) {
      el.classList.add('pdf-link');
      return;
    }

    if (
      href.match(/\.(ppsx|pptx|pptm|potx)$/i) ||
      download?.match(/\.(ppsx|pptx|pptm|potx)$/i)
    ) {
      el.classList.add('ppt-link');
      return;
    }

    if (
      href.match(/\.(eml|docx|msg)$/i) ||
      download?.match(/\.(eml|docx|msg)$/i)
    ) {
      el.classList.add('file-link');
      return;
    }
    return;
  }

  /**
   * Adds a visually hidden message to external links.
   *
   * This function checks if the provided HTML element has text content. If it does, the function
   * creates a new 'span' element, adds the CSS class "visually-hidden" to it, and sets its inner HTML
   * to the string '(opens in a new window)'. This 'span' element is then appended to the provided HTML
   * element. The purpose of this function is to enhance accessibility by providing screen reader users
   * with additional context about the behavior of external links.
   *
   * @param {HTMLElement} el - The HTML element to which the external link message should be added.
   */
  private handleExternalLinks(el: HTMLElement) {
    if (!(el.getAttribute('target') === '_blank') || !el.textContent) {
      return;
    }

    const externalLinkText = document.createElement('span');
    externalLinkText.classList.add('visually-hidden');
    externalLinkText.innerHTML = '(opens in a new window)';
    el.append(externalLinkText);
  }

  private enhanceLightDomContent() {
    const host = this as unknown as HTMLElement;

    host.querySelectorAll<HTMLAnchorElement>('a').forEach(anchor => {
      // Adds appropriate icons for links which go to files.
      this.handleFileLinks(anchor);
      this.handleExternalLinks(anchor);
    });

    host.querySelectorAll<HTMLTableElement>('table').forEach(table => {
      this.enhanceScrollableTable(table);
    });
  }

  private enhanceScrollableTable(table: HTMLTableElement) {
    if (!table.hasAttribute('tabindex')) {
      table.setAttribute('tabindex', '0');
    }

    if (!table.hasAttribute('role')) {
      table.setAttribute('role', 'table');
    }

    const caption = table.querySelector(':scope > caption');

    if (caption && !caption.id) {
      caption.id = `${componentName}-caption-${tableInstructionId++}`;
    }

    if (caption && !table.hasAttribute('aria-labelledby')) {
      table.setAttribute('aria-labelledby', caption.id);
    }

    if (!table.hasAttribute('aria-describedby')) {
      const instructionsId = `${componentName}-scroll-instructions-${tableInstructionId++}`;
      const instructions = document.createElement('span');
      instructions.id = instructionsId;
      instructions.classList.add('visually-hidden');
      instructions.textContent =
        'Scrollable table: use your keyboard or swipe to view additional columns.';
      table.insertAdjacentElement('afterend', instructions);
      table.setAttribute('aria-describedby', instructionsId);
    }
  }

  render(): TemplateResult {
    return html`<slot @slotchange=${this.handleSlotChange}></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-ckeditor': OutlineCkeditor;
  }
}
